import { Component, OnInit, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { OperatorElearningOverviewCategoriesService } from '../../../../../api/api-services/operator-elearning-overview-categories.service';
import { OperatorElearningTopicCategoriesService } from '../../../../../api/api-services/operator-elearning-topic-categories.service';
import { OperatorElearningTopicsService } from '../../../../../api/api-services/operator-elearning-topics.service';
import { OperatorAvailableExamsService } from '../../../../../api/api-services/operator-available-exams.service';
import { IExamViewModel } from '../../../../../api/exam-view-model.model';
import { IOperatorElearningOverviewCategoryVm } from '../../../../../api/operator-elearning-overview-category-vm.model';
import { IOperatorElearningOverviewTopicVm } from '../../../../../api/operator-elearning-overview-topic-vm.model';
import { IOperatorElearningTopicContentVm } from '../../../../../api/operator-elearning-topic-content-vm.model';
import { IOperatorElearningTopicCategoryVm } from '../../../../../api/operator-elearning-topic-category-vm.model';
import { IOperatorElearningTopicVm } from '../../../../../api/operator-elearning-topic-vm.model';
import { IDetailsControl } from '../../../../shared/models/details-control';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-overview-list',
  templateUrl: './overview-list.component.html',
  styleUrl: './overview-list.component.scss',
})
export class OverviewListComponent implements OnInit {
  private _operatorElearningOverviewCategoriesService = inject(OperatorElearningOverviewCategoriesService);
  private _operatorElearningTopicCategoriesService = inject(OperatorElearningTopicCategoriesService);
  private _operatorElearningTopicsService = inject(OperatorElearningTopicsService);
  private _operatorAvailableExamsService = inject(OperatorAvailableExamsService);

  public categories: IOperatorElearningOverviewCategoryVm[] = [];
  public isLoading = false;

  public categoryOptions: { label: string; value: number }[] = [];
  public topicOptions: { label: string; value: number }[] = [];
  public examOptions: IExamViewModel[] = [];

  public searchValue = '';
  public selectedCategoryIds: number[] = [];
  public selectedTopicIds: number[] = [];
  public selectedExamGuids: string[] = [];

  public expandedCategories: Record<string, boolean> = {};
  public expandedTopics: Record<string, boolean> = {};

  public selectedCategory!: IOperatorElearningTopicCategoryVm;
  public categoryIndex = 0;
  public categoryDetailsControl: IDetailsControl = { isEditing: false, isOpen: false };

  public selectedTopic!: IOperatorElearningTopicVm;
  public topicIndex = 0;
  public topicSiblings: IOperatorElearningOverviewTopicVm[] = [];
  public topicDetailsControl: IDetailsControl = { isEditing: false, isOpen: false };

  public selectedSlide!: IOperatorElearningTopicContentVm;
  public slideIndex = 0;
  public slideSiblings: IOperatorElearningTopicContentVm[] = [];
  public slideDetailsControl: IDetailsControl = { isEditing: false, isOpen: false };

  private _searchSubject = new Subject<string>();

  ngOnInit(): void {
    this.loadFilterOptions();
    this.load();
    this._searchSubject.pipe(debounceTime(400)).subscribe(() => this.load());
  }

  private loadFilterOptions(): void {
    this._operatorElearningTopicCategoriesService.get().subscribe({
      next: (categories) => {
        this.categoryOptions = categories.map((c) => ({ label: c.name, value: c.id }));
      },
    });
    this._operatorElearningTopicsService.get().subscribe({
      next: (topics) => {
        this.topicOptions = topics.map((t) => ({ label: t.name, value: t.id }));
      },
    });
    this._operatorAvailableExamsService.get().subscribe({
      next: (exams) => {
        this.examOptions = exams;
      },
    });
  }

  public load(): void {
    this.isLoading = true;
    this._operatorElearningOverviewCategoriesService
      .get(
        this.searchValue || undefined,
        this.selectedCategoryIds.length ? this.selectedCategoryIds : undefined,
        this.selectedTopicIds.length ? this.selectedTopicIds : undefined,
        this.selectedExamGuids.length ? this.selectedExamGuids : undefined
      )
      .subscribe({
        next: (categories) => {
          this.categories = categories;
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  public onSearchChange(): void {
    this._searchSubject.next(this.searchValue);
  }

  public onFilterChange(): void {
    this.load();
  }

  public getExamNames(category: IOperatorElearningOverviewCategoryVm): string {
    if (category.examCategories && category.examCategories.length > 0) {
      return category.examCategories.map((exam: any) => exam.examName).join(', ');
    }
    return 'Brak przypisanych kategorii';
  }

  public getSlideExamNames(slide: IOperatorElearningTopicContentVm): string {
    if (slide.examCategories && slide.examCategories.length > 0) {
      return slide.examCategories.map((exam) => exam.examName).join(', ');
    }
    return 'Wszystkie kategorie działu';
  }

  public getSlideImageUrl(imageLink: string): string {
    return environment.webApiUrl + imageLink.replace('/media', '/uploadMedia');
  }

  public onCategoryRowExpand(event: { data: IOperatorElearningOverviewCategoryVm }): void {
    this.expandedCategories[event.data.id] = true;
  }

  public onCategoryRowCollapse(event: { data: IOperatorElearningOverviewCategoryVm }): void {
    delete this.expandedCategories[event.data.id];
  }

  public onTopicRowExpand(event: { data: { id: number } }): void {
    this.expandedTopics[event.data.id] = true;
  }

  public onTopicRowCollapse(event: { data: { id: number } }): void {
    delete this.expandedTopics[event.data.id];
  }

  public openCategoryDetails(category: IOperatorElearningOverviewCategoryVm): void {
    this.selectedCategory = category;
    this.categoryIndex = this.categories.findIndex((c) => c.id === category.id);
    this.categoryDetailsControl = { isEditing: false, isOpen: true };
  }

  public openTopicDetails(category: IOperatorElearningOverviewCategoryVm, topic: IOperatorElearningOverviewTopicVm): void {
    this.selectedTopic = topic;
    this.topicSiblings = [...category.topics];
    this.topicIndex = category.topics.findIndex((t) => t.id === topic.id);
    this.topicDetailsControl = { isEditing: false, isOpen: true };
  }

  public openSlideDetails(topic: IOperatorElearningOverviewTopicVm, slide: IOperatorElearningTopicContentVm): void {
    this.selectedSlide = slide;
    this.slideSiblings = [...topic.slides];
    this.slideIndex = topic.slides.findIndex((s) => s.id === slide.id);
    this.slideDetailsControl = { isEditing: false, isOpen: true };
  }
}
